import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Linkedin, Menu, X } from "lucide-react";
import { BRAND, buildWhatsappLink } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

const LINKS = [
  { label: "Início", href: "#inicio" },
  { label: "Sobre", href: "#sobre" },
  { label: "Para sua empresa", href: "#solucoes" },
  { label: "Agendamento", href: "#agendamento" },
  { label: "Contatos", href: "#contatos" },
];

const WhatsappIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.671 5.476l-.999 3.648 3.827-1.003zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
  </svg>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[68px] flex items-center justify-between">
        {/* Logo + name */}
        <a href="#inicio" data-testid="navbar-logo" className="flex items-center group">
          <img src={BRAND.logo} alt="EFORY" className="h-7 md:h-8 w-auto object-contain" />
        </a>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-0 after:bg-efory-blue hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Socials + CTA */}
        <div className="flex items-center gap-2">
          <a
            href={BRAND.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-instagram"
            className="h-9 w-9 hidden sm:flex items-center justify-center rounded-full border border-white/15 text-white/80 hover:text-white hover:border-efory-blue hover:bg-efory-blue/10 transition-all duration-200"
            aria-label="Instagram"
          >
            <Instagram className="h-[18px] w-[18px]" />
          </a>
          <a
            href={BRAND.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-linkedin"
            className="h-9 w-9 hidden sm:flex items-center justify-center rounded-full border border-white/15 text-white/80 hover:text-white hover:border-[#0A66C2] hover:bg-[#0A66C2]/15 transition-all duration-200"
            aria-label="LinkedIn"
          >
            <Linkedin className="h-[18px] w-[18px]" />
          </a>
          <a
            href={buildWhatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp"
            className="h-9 w-9 hidden sm:flex items-center justify-center rounded-full border border-white/15 text-white/80 hover:text-white hover:border-[#25D366] hover:bg-[#25D366]/15 transition-all duration-200"
            aria-label="WhatsApp"
          >
            <WhatsappIcon className="h-[18px] w-[18px]" />
          </a>
          <a href="#agendamento" className="hidden md:block">
            <Button
              data-testid="navbar-cta-button"
              className="bg-efory-blue hover:bg-efory-bluedark text-white rounded-full px-5 font-semibold transition-all duration-300 hover:scale-105"
            >
              Diagnóstico em 48h
            </Button>
          </a>

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="lg:hidden h-10 w-10 flex items-center justify-center rounded-md text-white"
                aria-label="Abrir menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-efory-ink border-l border-white/10 text-white w-[280px]">
              <div className="mb-10 mt-2">
                <img src={BRAND.logo} alt="EFORY" className="h-8 w-auto object-contain" />
              </div>
              <nav className="flex flex-col gap-1">
                {LINKS.map((l) => (
                  <SheetClose asChild key={l.href}>
                    <a
                      href={l.href}
                      data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                      className="py-3 px-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      {l.label}
                    </a>
                  </SheetClose>
                ))}
              </nav>
              <div className="mt-8 flex gap-3">
                <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer" className="h-11 w-11 flex items-center justify-center rounded-full border border-white/15 hover:bg-efory-blue/15" aria-label="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href={BRAND.linkedin} target="_blank" rel="noopener noreferrer" className="h-11 w-11 flex items-center justify-center rounded-full border border-white/15 hover:bg-[#0A66C2]/15" aria-label="LinkedIn">
                  <Linkedin className="h-5 w-5" />
                </a>
                <a href={buildWhatsappLink()} target="_blank" rel="noopener noreferrer" className="h-11 w-11 flex items-center justify-center rounded-full border border-white/15 hover:bg-[#25D366]/15" aria-label="WhatsApp">
                  <WhatsappIcon className="h-5 w-5" />
                </a>
              </div>
              <SheetClose asChild>
                <a href="#agendamento">
                  <Button className="mt-8 w-full bg-efory-blue hover:bg-efory-bluedark rounded-full font-semibold" data-testid="mobile-cta-button">
                    Diagnóstico em 48h
                  </Button>
                </a>
              </SheetClose>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
}

export { WhatsappIcon };
